import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Grid, Maximize2, ChevronDown, ChevronUp } from 'lucide-react';
import { propertyData } from '../../data/mock';

const GallerySection = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(true);

  // Images 62-74 have been removed from mock.js
  const totalImages = propertyData.images.gallery.length;
  const displayImages = showAll ? propertyData.images.gallery : propertyData.images.gallery.slice(0, 12);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  }, [totalImages]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  }, [totalImages]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goNext, goPrev, closeLightbox]);

  // Touch/swipe for lightbox
  const [touchStart, setTouchStart] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
  };

  return (
    <section id="gallery" className="py-24 bg-[#0d1117]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Photo Gallery
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            Experience the Estate
          </h2>
          <p className="text-lg text-white/70 font-light">
            Explore every detail of this exceptional property through our comprehensive gallery of {totalImages} photos
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {displayImages.map((img, index) => (
            <div
              key={img}
              className={`relative group cursor-pointer overflow-hidden rounded-xl ${
                index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
              }`}
              onClick={() => openLightbox(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={img}
                alt={`Property view ${index + 1}`}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                  index === 0 ? 'h-[300px] sm:h-[400px]' : 'h-36 sm:h-44'
                }`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center pointer-events-none">
                <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded px-2 py-0.5 text-white/80 text-xs">
                {index + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Toggle */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center px-6 py-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/50 transition-all duration-300"
            data-testid="gallery-toggle-btn"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-5 h-5 mr-2" />
                Show Less Photos
              </>
            ) : (
              <>
                <Grid className="w-5 h-5 mr-2" />
                View All {totalImages} Photos
              </>
            )}
          </button>
          <p className="text-white/50 text-sm mt-3">
            Showing {displayImages.length} of {totalImages} photos • Click any image to enlarge
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          data-testid="gallery-lightbox"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close gallery"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Previous Button */}
          <button
            onClick={goPrev}
            className="absolute left-2 sm:left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Main Image */}
          <div className="max-w-5xl max-h-[80vh] mx-16 sm:mx-20">
            <img
              src={propertyData.images.gallery[currentIndex]}
              alt={`Property view ${currentIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-center text-white/60 mt-4">
              Photo {currentIndex + 1} of {totalImages}
            </p>
          </div>

          {/* Next Button */}
          <button
            onClick={goNext}
            className="absolute right-2 sm:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Thumbnails Strip */}
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 overflow-x-auto max-w-[90vw] pb-2 px-4">
            {propertyData.images.gallery.slice(
              Math.max(0, currentIndex - 5),
              Math.min(totalImages, currentIndex + 6)
            ).map((img, idx) => {
              const actualIndex = Math.max(0, currentIndex - 5) + idx;
              return (
                <button
                  key={actualIndex}
                  onClick={() => setCurrentIndex(actualIndex)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    currentIndex === actualIndex ? 'border-amber-500 opacity-100' : 'border-transparent opacity-50 hover:opacity-75'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>

          {/* Keyboard hint */}
          <p className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/30 text-xs hidden sm:block">
            Use ← → arrow keys to navigate • ESC to close
          </p>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
